import { useContext, useEffect, useState } from 'react';

import {
  Flex,
  FormControl,
  FormErrorMessage,
  IconButton,
  NumberInputProps,
  Text,
  useMultiStyleConfig,
} from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { BiCheckCircle, BiEditAlt, BiXCircle } from 'react-icons/bi';

import { Deal, DealStateEnum } from '../../../gql/dealGql';

import NumberInput from '../../shared/NumberInput';
import { DebouncedSave } from '../DealInfoBuyoutForm';

import { PermissionEnum } from '../../../constants/permissions';
import { DealContext } from '../../../libs/DealContext';
import { AbilityContext } from '../../../libs/contextLib';

type BuyRateProps = {
  dealDetailsPageBeingEdited: boolean;
  debouncedSave: DebouncedSave;
  fontSize?: Pick<NumberInputProps, 'fontSize'>['fontSize'];
};

export const BuyRate = ({
  dealDetailsPageBeingEdited,
  debouncedSave,
  fontSize = 'sm',
}: BuyRateProps) => {
  const { deal } = useContext(DealContext);
  const abilities = useContext(AbilityContext);

  const { values, errors, setFieldValue } = useFormikContext<Deal>();

  const [clickedEditBuyRate, setClickedEditBuyRate] = useState(false);

  const styles = useMultiStyleConfig('FormControl');

  const canBuildDeal = abilities.has(PermissionEnum.BuildDeal);
  const inEditableState =
    deal.state === DealStateEnum.Structuring ||
    deal.state === DealStateEnum.Closing ||
    deal.state === DealStateEnum.Signed;
  const canEditBuyRate =
    abilities.has(PermissionEnum.EditBuyRate) && (dealDetailsPageBeingEdited || inEditableState);

  const handleSaveBuyRateClick = async () => {
    setFieldValue('financial_info.buy_rate', values.financial_info.newBuyRate ?? 0);

    debouncedSave();
  };

  // Close edit mode after saving and keep the fields in sync.
  useEffect(() => {
    if (!clickedEditBuyRate) {
      return;
    }

    setFieldValue('financial_info.newBuyRate', values.financial_info.buy_rate);
    setClickedEditBuyRate(false);
  }, [values.financial_info.buy_rate]);

  // Keeps the fields in sync when not editing.
  useEffect(() => {
    if (!clickedEditBuyRate && values.financial_info.newBuyRate === undefined) {
      setFieldValue('financial_info.newBuyRate', values.financial_info.buy_rate);
    }
  }, [clickedEditBuyRate, values.financial_info.buy_rate, values.financial_info.newBuyRate]);

  if (!canBuildDeal) {
    return null;
  }

  if (canEditBuyRate && clickedEditBuyRate) {
    return (
      <Flex>
        <NumberInput name="financial_info.newBuyRate" isPercentage showThousandSeparator />
        <Flex alignItems="top" mt={1} ml={1}>
          <IconButton
            icon={<BiXCircle />}
            variant="iconHover"
            aria-label="Cancel"
            onClick={() => {
              setClickedEditBuyRate(false);
            }}
            size="xs"
          />
          <IconButton
            icon={<BiCheckCircle />}
            variant="iconHover"
            size="xs"
            aria-label="Save Buy Rate"
            isDisabled={
              !!errors.financial_info?.newBuyRate ||
              (values.financial_info.buy_rate ?? 0) === (values.financial_info.newBuyRate ?? 0)
            }
            onClick={handleSaveBuyRateClick}
          />
        </Flex>
      </Flex>
    );
  }

  return (
    <Flex alignItems="center" pt={2} pl={3}>
      <FormControl isInvalid={!!errors.financial_info?.buy_rate}>
        <Flex alignItems="center">
          <Text fontWeight="normal" fontSize={fontSize}>
            {values.financial_info?.buy_rate?.toFixed(2) ?? '0.00'}%
          </Text>
          {canEditBuyRate ? (
            <IconButton
              icon={<BiEditAlt />}
              variant="iconHover"
              size="xs"
              aria-label="Edit Buy Rate"
              onClick={() => {
                setFieldValue('financial_info.newBuyRate', values.financial_info.buy_rate);
                setClickedEditBuyRate(true);
              }}
            />
          ) : null}
        </Flex>
        <FormErrorMessage sx={styles.error}>{errors.financial_info?.buy_rate}</FormErrorMessage>
      </FormControl>
    </Flex>
  );
};
